import { useState, useRef, useEffect, RefObject } from 'react';

// Type for the position calculations
type Boundary = {
  index: number;
  position: number;
};

export interface UseTimelineDragProps {
  initialSelectedRange: [number, number];
  eventRefs: RefObject<(HTMLDivElement | null)[]>;
  timelineRef: RefObject<HTMLDivElement>;
  eventsLength: number;
  onSelectionChange?: (range: [number, number]) => void;
  currentVersionDotRef?: RefObject<HTMLDivElement | null>;
}

export interface UseTimelineDragReturn {
  selectedRange: [number, number];
  previewRange: [number, number] | null;
  isDragging: boolean;
  handleHandlerMouseDown: (e: React.MouseEvent, isTopHandler: boolean) => void;
  handleEventDrag: (e: React.MouseEvent, eventIndex: number) => void;
  getHandlerPosition: (index: number) => number | undefined;
  positionsUpdateKey: number;
  setSelectedRange: (range: [number, number]) => void;
}

// Helper function to calculate position relative to container
const calculateRelativePosition = (
  ref: HTMLDivElement,
  containerRef: HTMLDivElement,
  positionType: 'top' | 'bottom' = 'top'
): number => {
  const containerRect = containerRef.getBoundingClientRect();
  const refRect = ref.getBoundingClientRect();

  return positionType === 'top'
    ? refRect.top - containerRect.top
    : refRect.bottom - containerRect.top;
};

export const useTimelineDrag = ({
  initialSelectedRange,
  eventRefs,
  timelineRef,
  eventsLength,
  onSelectionChange,
  currentVersionDotRef,
}: UseTimelineDragProps): UseTimelineDragReturn => {
  // State to track the selected range
  const [selectedRange, setSelectedRange] =
    useState<[number, number]>(initialSelectedRange);

  // State to track preview range during dragging
  const [previewRange, setPreviewRange] = useState<[number, number] | null>(
    null
  );

  // Track if we're currently dragging
  const [isDragging, setIsDragging] = useState(false);

  // Track the drag start event index
  const dragStartIndexRef = useRef<number>(-1);

  // Force re-render when positions need to be updated
  const [positionsUpdateKey, setPositionsUpdateKey] = useState(0);

  // Keep track of the new range to apply when releasing
  const newRangeRef = useRef<[number, number] | null>(null);

  // Track the current mouse Y position for better drag calculations
  const dragMouseYRef = useRef<number>(0);

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRange);
    }
  }, [selectedRange, onSelectionChange]);

  // Get event boundaries (helper function)
  const getEventBoundaries = (): Boundary[] => {
    const boundaries: Boundary[] = [];

    if (!timelineRef.current) return boundaries;

    const containerRect = timelineRef.current.getBoundingClientRect();
    const refs = eventRefs.current || [];

    // Add top positions of all events
    refs.forEach((ref, index) => {
      if (!ref) return;

      const rect = ref.getBoundingClientRect();
      const topPosition = rect.top - containerRect.top;

      boundaries.push({ index, position: topPosition });

      // For the last event, also add its bottom boundary
      if (index === eventsLength - 1) {
        const bottomPosition = rect.bottom - containerRect.top;
        boundaries.push({ index: index + 1, position: bottomPosition });
      }
    });

    return boundaries.sort((a, b) => a.position - b.position);
  };

  // Find closest boundary to a position (helper function)
  const findClosestBoundary = (
    relativeY: number,
    boundaries: Boundary[]
  ): Boundary => {
    let closestBoundary = { index: 0, position: 0 };
    let minDistance = Infinity;

    for (const boundary of boundaries) {
      const distance = Math.abs(relativeY - boundary.position);
      if (distance < minDistance) {
        minDistance = distance;
        closestBoundary = boundary;
      }
    }

    return closestBoundary;
  };

  // Find the index of the event closest to a given Y position
  const findClosestEventIndex = (clientY: number): number => {
    if (!timelineRef.current) return 0;

    const containerRect = timelineRef.current.getBoundingClientRect();
    const relativeY = clientY - containerRect.top;

    // Get event boundaries
    const boundaries = getEventBoundaries();

    // Find the closest boundary
    const closestBoundary = findClosestBoundary(relativeY, boundaries);

    return closestBoundary.index;
  };

  // Calculate the new range based on a drag event
  const calculateNewRange = (
    clientY: number,
    isTopHandler: boolean
  ): [number, number] => {
    const newIndex = findClosestEventIndex(clientY);
    const baseRange = isDragging && previewRange ? previewRange : selectedRange;
    const newRange = [...baseRange] as [number, number];

    if (isTopHandler) {
      // Top handler can't go below bottom handler
      newRange[0] = Math.min(newIndex, baseRange[1] - 1);
    } else {
      // Bottom handler can't go above top handler
      newRange[1] = Math.max(newIndex, baseRange[0] + 1);
    }

    return newRange;
  };

  // Helper for calculating downward drag range
  const calculateDownwardDragRange = (
    startIndex: number,
    currentIndex: number
  ): [number, number] => {
    // If we're at the first event (index 0), just use currentIndex
    if (currentIndex === 0) {
      return [startIndex, currentIndex + 1];
    }

    // Get the boundaries to check positions more accurately
    const boundaries = getEventBoundaries();

    // Find the boundary for the current index and the one before it
    const currentBoundary = boundaries.find((b) => b.index === currentIndex);
    const previousBoundary = boundaries.find(
      (b) => b.index === currentIndex - 1
    );

    if (currentBoundary && previousBoundary && timelineRef.current) {
      const containerRect = timelineRef.current.getBoundingClientRect();
      const mouseY = dragMouseYRef.current - containerRect.top;

      // Calculate the midpoint between the previous event bottom and current event top
      const midpoint =
        (previousBoundary.position + currentBoundary.position) / 2;

      // If the mouse is below the midpoint, use the current index
      // Otherwise, use the previous index
      if (mouseY >= midpoint) {
        return [startIndex, currentIndex]; // Include current event (changed based on user feedback)
      } else {
        return [startIndex, currentIndex]; // Don't include current event
      }
    }

    // Fallback to simplified behavior if we can't determine more precisely
    return [startIndex, currentIndex];
  };

  // Helper for calculating upward drag range
  const calculateUpwardDragRange = (
    startIndex: number,
    currentIndex: number
  ): [number, number] => {
    // Ensure we never return a zero-length range
    // Always include the starting event (startIndex)
    if (currentIndex === startIndex) {
      return [currentIndex, startIndex + 1]; // Default range when at the same event
    }

    // Get the boundaries to check positions more accurately
    const boundaries = getEventBoundaries();

    // If we're dragging upward, currentIndex should always be less than startIndex
    if (currentIndex < startIndex) {
      // Find the boundary for the current index and the one after it
      const currentBoundary = boundaries.find((b) => b.index === currentIndex);
      const nextBoundary = boundaries.find((b) => b.index === currentIndex + 1);

      if (currentBoundary && nextBoundary && timelineRef.current) {
        const containerRect = timelineRef.current.getBoundingClientRect();
        const mouseY = dragMouseYRef.current - containerRect.top;

        // Calculate the midpoint between the current event bottom and next event top
        const midpoint = (currentBoundary.position + nextBoundary.position) / 2;

        // If the mouse is above the midpoint, include the current event
        // Otherwise, start from the next event, but only if that won't create a zero-length range
        if (mouseY <= midpoint) {
          return [currentIndex, startIndex + 1]; // Include current event in upward selection
        } else {
          // Only use currentIndex + 1 if it's not equal to startIndex (which would create zero-length range)
          const adjustedIndex =
            currentIndex + 1 < startIndex ? currentIndex + 1 : currentIndex;
          return [adjustedIndex, startIndex + 1];
        }
      }
    }

    // Always include the starting event in the range
    return [currentIndex, startIndex + 1];
  };

  // Calculate range between two event indexes for event dragging
  const calculateEventDragRange = (
    startIndex: number,
    currentIndex: number
  ): [number, number] => {
    // For dragging downwards (or staying at the same event)
    if (startIndex <= currentIndex) {
      return calculateDownwardDragRange(startIndex, currentIndex);
    }
    // For dragging upwards
    else {
      return calculateUpwardDragRange(startIndex, currentIndex);
    }
  };

  // Handle dragging from an event
  const handleEventDrag = (e: React.MouseEvent, eventIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    // Start dragging - enable preview mode
    setIsDragging(true);

    // Set initial preview range to just this event
    const initialRange: [number, number] = [eventIndex, eventIndex + 1];
    setPreviewRange(initialRange);

    // Store the starting event index
    dragStartIndexRef.current = eventIndex;
    // Store initial mouse position
    dragMouseYRef.current = e.clientY;
    newRangeRef.current = null;

    const handleMouseMove = (e: MouseEvent) => {
      const currentIndex = findClosestEventIndex(e.clientY);
      // Update current mouse position
      dragMouseYRef.current = e.clientY;

      // Calculate the range between the start event and current event
      const newRange = calculateEventDragRange(
        dragStartIndexRef.current,
        currentIndex
      );

      // Safety check to ensure we never set an invalid range
      if (newRange[0] < newRange[1]) {
        setPreviewRange(newRange);
        newRangeRef.current = newRange;
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      const finalRange = newRangeRef.current || initialRange;

      // Apply the new selection
      setTimeout(() => {
        if (JSON.stringify(finalRange) !== JSON.stringify(selectedRange)) {
          setSelectedRange(finalRange);
        }

        // End dragging
        setIsDragging(false);
        setPreviewRange(null);
        dragStartIndexRef.current = -1;
      }, 10);

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle dragging of the handler
  const handleHandlerMouseDown = (
    e: React.MouseEvent,
    isTopHandler: boolean
  ) => {
    e.preventDefault();

    // Start dragging - enable preview mode
    setIsDragging(true);

    // Initialize preview range from current selection
    const startRange = [...selectedRange] as [number, number];
    setPreviewRange(startRange);
    newRangeRef.current = null;

    const handleMouseMove = (e: MouseEvent) => {
      const newRange = calculateNewRange(e.clientY, isTopHandler);

      // Update preview and store the latest range to apply on mouse up
      setPreviewRange(newRange);
      newRangeRef.current = newRange;
    };

    const handleMouseUp = (e: MouseEvent) => {
      // Get the final range, either from our tracked ref or calculate it
      const finalRange =
        newRangeRef.current || calculateNewRange(e.clientY, isTopHandler);

      // Apply the new selection with a slight delay to ensure the state update happens
      setTimeout(() => {
        if (JSON.stringify(finalRange) !== JSON.stringify(selectedRange)) {
          setSelectedRange(finalRange);
        }

        // End dragging
        setIsDragging(false);
        setPreviewRange(null);
      }, 10);

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Helper functions for getHandlerPosition
  const getFirstEventPosition = (): number | undefined => {
    // Use currentVersionDotRef for the top handler when selecting the first event
    if (currentVersionDotRef?.current) {
      const container = timelineRef.current;
      if (!container) return undefined;

      const ref = currentVersionDotRef.current;
      // Position the handler at the top of the current version component
      // Use a percentage-based offset to ensure consistency across zoom levels
      const position = calculateRelativePosition(ref, container, 'top');
      const refRect = ref.getBoundingClientRect();
      const offsetPercentage = 0.2; // 20% of the component height
      const dynamicOffset = refRect.height * offsetPercentage;
      return Math.round(position - dynamicOffset);
    }

    if (!timelineRef.current || !eventRefs.current || !eventRefs.current[0])
      return undefined;

    const container = timelineRef.current;
    const ref = eventRefs.current[0];
    if (!ref) return undefined;

    return calculateRelativePosition(ref, container, 'top');
  };

  const getLastEventPosition = (): number | undefined => {
    if (!timelineRef.current || !eventRefs.current) return undefined;

    const lastRef = eventRefs.current[eventsLength - 1];
    if (!lastRef) return undefined;

    const container = timelineRef.current;
    return calculateRelativePosition(lastRef, container, 'bottom');
  };

  const getMiddleEventPosition = (index: number): number | undefined => {
    if (!timelineRef.current || !eventRefs.current) return undefined;

    const ref = eventRefs.current[index];
    if (!ref) return undefined;

    const container = timelineRef.current;
    return calculateRelativePosition(ref, container, 'top');
  };

  // Get position of a handler in pixels
  const getHandlerPosition = (index: number): number | undefined => {
    if (
      !timelineRef.current ||
      !eventRefs.current ||
      eventRefs.current.length === 0
    )
      return undefined;

    // For the top boundary of the first event
    if (index === 0) {
      return getFirstEventPosition();
    }

    // For the bottom boundary of the last event
    if (index === eventsLength) {
      return getLastEventPosition();
    }

    // For boundaries between events
    if (index > 0 && index < eventsLength) {
      return getMiddleEventPosition(index);
    }

    return undefined;
  };

  return {
    selectedRange,
    previewRange,
    isDragging,
    handleHandlerMouseDown,
    handleEventDrag,
    getHandlerPosition,
    positionsUpdateKey,
    setSelectedRange,
  };
};

export default useTimelineDrag;
